import { useRef, useEffect, useState } from "react";
import { Container, Typography, Stack, Divider, Box } from "@mui/material";
import { useReactToPrint } from "react-to-print";
import numberToWords from "number-to-words";
import { useNavigate, useLocation } from "react-router";
import dayjs from "dayjs";
import QrCodeGenerator from "views/commonComponents/QrCode/QrCodeGenerator";
import { useSelector } from "react-redux";

const A4Print = () => {
  const componentRef = useRef();
  const location = useLocation();
  const navigate = useNavigate();
  const { data, tableData, qrData, typ, navigateBack } = location.state;
  const company = useSelector((state) => state.common.companyInfo);
  const companyLogo = company.logo ? JSON.parse(company.logo)?.img : null;
  const decimalCount = useSelector((state) => state.common.decimalCount);
  const primaryCurrency = useSelector((state) => state.common.primaryCurrency);
  const subCurrency = useSelector((state) => state.common.subCurrency);
  const isQrShow = useSelector((state) => state.common.QrShowPrint);
  const commonState = useSelector((state) => state.common);
  const [sections, setSections] = useState([]);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onAfterPrint: () => {
      navigate(navigateBack);
    },
  });

  useEffect(() => {
    if (company) {
      const timeoutId = setTimeout(() => {
        handlePrint();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  useEffect(() => {
    if (tableData && tableData.length > 0) {
      const generatedSections = generateSections(tableData);
      setSections(generatedSections);
    }
  }, [tableData]);

  function convertNumberToWordsWithCurrency(number) {
    const paisaAmount = (number * 100) % 100;
    const words = numberToWords.toWords(Math.floor(number));
    const paisaInWords = numberToWords.toWords(paisaAmount);
    return `${words} ${primaryCurrency} and  ${paisaInWords} ${subCurrency}`;
  }

  const amount = data.BillNetTotalField;
  const result = convertNumberToWordsWithCurrency(amount);

  const calculateItemsPerPage = (items) => {
    // Constants for A4 page measurements
    const A4_HEIGHT_CM = 29.7;
    const HEADER_HEIGHT_CM = 8.0;
    const TABLE_HEADER_HEIGHT_CM = 1.5;
    const FOOTER_HEIGHT_CM = 8.0;

    const availableSpace =
      A4_HEIGHT_CM -
      HEADER_HEIGHT_CM -
      TABLE_HEADER_HEIGHT_CM -
      FOOTER_HEIGHT_CM;

    let currentHeight = 0;
    let itemsPerPage = 0;

    // Base row height for items without long descriptions
    const baseRowHeight = 0.7; // cm

    for (const item of items) {
      // Estimate height based on description length
      const description = item.ItemNameTextField || "";
      // More accurate line count estimation for print
      const lineCount = Math.ceil(description.length / 35);
      const itemHeight = baseRowHeight * Math.max(1, lineCount);

      if (currentHeight + itemHeight > availableSpace && itemsPerPage > 0) {
        break;
      }

      currentHeight += itemHeight;
      itemsPerPage++;
    }

    return Math.max(1, itemsPerPage);
  };

  const generateSections = (tableData) => {
    const sections = [];

    // Assign unique IDs to each product
    const updatedTableData = tableData.map((item, index) => ({
      ...item,
      id: index + 1,
    }));

    let currentIndex = 0;
    let pageNumber = 1;
    const totalItems = updatedTableData.length;

    while (currentIndex < totalItems) {
      // Calculate how many items can fit on this page
      const remainingItems = updatedTableData.slice(currentIndex);
      const itemsThisPage = calculateItemsPerPage(remainingItems);

      const sectionItems = updatedTableData.slice(
        currentIndex,
        currentIndex + itemsThisPage
      );

      sections.push(
        renderSection(
          sectionItems,
          pageNumber,
          Math.ceil(totalItems / itemsThisPage),
          currentIndex === 0 // isFirstPage
        )
      );

      currentIndex += itemsThisPage;
      pageNumber++;
    }

    return sections;
  };

  const renderSection = (
    sectionItems,
    currentPage,
    totalPages,
    isFirstPage
  ) => {
    return (
      <div
        key={`section-${currentPage}`}
        style={{
          marginTop: currentPage !== 1 ? "1rem" : "0",
          pageBreakBefore: currentPage !== 1 ? "always" : "auto",
          color: "black",
          borderColor: "black",
          width: "100%",
          padding: "2px",
          fontFamily: "'Roboto', sans-serif",
        }}
      >
        <div style={{ border: "1px solid black", position: "relative" }}>
          {/* Header Section - Appears on every page */}
          <Stack  direction="row" sx={{ height: "17rem" }}>
            <div
              style={{
                width: "45%",
                borderRight: "1px solid black",
                height: "17rem",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-around",
                overflow: "hidden",
              }}
            >
              <div
                style={{ width: "13.3rem", height: "30%", margin: "1rem" }}
                onClick={() => {
                  navigate("/sales/orderForm");
                }}
              >
                <img
                  src={companyLogo}
                  style={{
                    objectFit: "contain",
                    height: "100%",
                    width: "100%",
                  }}
                  alt="Company Logo"
                />
              </div>

              <Container
                disableGutters
                sx={{ padding: "0.3rem", color: "black" }}
              >
                <Typography
                  variant="h4"
                  sx={{ fontSize: "1.5rem", fontWeight: "bold" }}
                >
                  {company.Name}
                </Typography>
                <Typography sx={{ fontSize: "0.9rem" }}>
                  {company.Add1}
                </Typography>
                <Typography sx={{ fontSize: "0.9rem" }}>
                  {(company.city_name, company.Place)}
                </Typography>
                <Typography sx={{ fontSize: "0.9rem" }}>
                  {" "}
                  {company.Country}
                </Typography>
                <Typography sx={{ fontSize: "0.9rem" }}>
                  {" "}
                  Zipcode{company.Postal}
                </Typography>
                <Typography sx={{ fontSize: "0.9rem" }}>
                  Ph : {company.Ph} | VAT: {company.Add2}
                </Typography>

                <Typography sx={{ fontSize: "0.9rem" }}>
                  {" "}
                  {company.Web}
                </Typography>
              </Container>
            </div>
            <Stack direction="column" sx={{ width: "55%", height: "15.6rem" }}>
              <Typography
                align="center"
                variant="h4"
                sx={{
                  height: "1.5rem",
                  fontSize: "1.5rem",
                  fontWeight: "bold",
                }}
              >
                {typ === "SB"
                  ? "Tax Invoice"
                  : typ === "OF"
                  ? "Order Form"
                  : "Sales Return"}
              </Typography>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  borderColor: "black",
                }}
              >
                <tbody style={{ padding: "10px", borderColor: "black" }}>
                  <tr>
                    <td
                      style={{
                        border: "1px solid black",
                        height: "1.5rem",
                        padding: "0.2rem",
                      }}
                    >
                      <Typography sx={{ fontSize: "0.9rem" }}>
                        {data.BillNo
                          ? typ === "OF"
                            ? `Voucher No: ${data.NewBillNo}`
                            : typ === "B2C"
                            ? `Invoice No: ${data.prefix}`
                            : `Invoice No: ${data.NewBillNo}`
                          : `Return No: ${data.RetNo}`}
                      </Typography>
                    </td>
                    <td
                      style={{
                        height: "1.5rem",
                        padding: 0,
                        border: "1px solid black",
                      }}
                    >
                      <div style={{ display: "flex", height: "100%" }}>
                        <Typography
                          sx={{
                            width: "80%",
                            display: "flex",
                            alignItems: "center",
                            fontSize: "0.8rem",
                            p: 0.2,
                          }}
                        >
                          Original for Recipient
                        </Typography>
                        <div
                          style={{
                            width: "20%",
                            height: "100%",
                            borderLeft: "1px solid black",
                          }}
                        ></div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{
                        border: "1px solid black",
                        height: "1.5rem",
                        padding: "0.2rem",
                      }}
                    >
                      <Typography sx={{ fontSize: "0.9rem" }}>
                        {typ === "OF" ? `Voucher Date:` : ` Invoice Date:`}
                        <b>{dayjs(data.BillDate).format("DD-MM-YYYY")}</b>
                      </Typography>
                    </td>
                    <td
                      style={{
                        height: "1.5rem",
                        padding: 0,
                        border: "1px solid black",
                      }}
                    >
                      <div style={{ display: "flex", height: "100%" }}>
                        <Typography
                          sx={{
                            width: "80%",
                            display: "flex",
                            alignItems: "center",
                            fontSize: "0.8rem",
                            p: 0.2,
                          }}
                        >
                          Duplicate for Transporter
                        </Typography>
                        <div
                          style={{
                            width: "20%",
                            height: "100%",
                            borderLeft: "1px solid black",
                          }}
                        ></div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{
                        border: "1px solid black",
                        padding: "0.2rem",
                      }}
                    >
                      <Typography sx={{ fontSize: "0.9rem" }}>
                        State Code: <b>0</b>
                      </Typography>
                    </td>
                    <td
                      style={{
                        height: "1.5rem",
                        padding: 0,
                        border: "1px solid black",
                      }}
                    >
                      <div style={{ display: "flex", height: "100%" }}>
                        <Typography
                          sx={{
                            width: "80%",
                            display: "flex",
                            alignItems: "center",
                            fontSize: "0.8rem",
                            p: 0.2,
                          }}
                        >
                          Triplicate for Supplier
                        </Typography>
                        <div
                          style={{
                            width: "20%",
                            height: "100%",
                            borderLeft: "1px solid black",
                          }}
                        ></div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{
                        border: "1px solid black",
                        padding: "0.2rem",
                      }}
                    >
                      <Typography sx={{ fontSize: "0.9rem" }}>
                        Client ID: <b>0</b>
                      </Typography>
                    </td>
                    <td
                      style={{
                        border: "1px solid black",
                        padding: "0.2rem",
                      }}
                    >
                      <Typography sx={{ fontSize: "0.9rem" }}>
                        Vehicle No: <b>0</b>
                      </Typography>
                    </td>
                  </tr>
                </tbody>
              </table>
              <Stack
                direction="row"
                justifyContent="space-between"
                spacing={0}
                sx={{ height: "100%", p: 1 }}
              >
                <Stack direction="column" alignItems={"start"} width={"80%"}>
                  <Typography sx={{ fontSize: "0.9rem" }}>
                    <b style={{ display: "block" }}>To :{data.BillPartyName}</b>{" "}
                    {data.CashP}
                  </Typography>
                  <Typography sx={{ fontSize: "0.9rem" }}>
                    {data.warranty}{" "}
                  </Typography>
                  <Typography sx={{ fontSize: "0.9rem" }}>
                    PH: {data.Ph}{" "}
                  </Typography>
                  <Typography sx={{ fontSize: "0.9rem" }}>
                    <b>VAT NO:</b> {data.vat_no}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="center" width={"20%"}>
                  <Box width={120}>
                    {isQrShow ? (
                      <QrCodeGenerator
                        qrData={data.zatca_einv_qr_code || qrData}
                      />
                    ) : null}
                  </Box>
                </Stack>
              </Stack>
            </Stack>
          </Stack>

          {/* Product Table with Header and Items */}
          <div style={{ minHeight: "30rem" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                tableLayout: "fixed",
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      textAlign: "left",
                      border: "1px solid black",
                      padding: "0.4rem",
                      fontSize: "0.9rem",
                      fontWeight: "bold",
                      width: "5%",
                    }}
                  >
                    SI NO
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      border: "1px solid black",
                      padding: "0.4rem",
                      fontSize: "0.9rem",
                      fontWeight: "bold",
                      width: "30%",
                    }}
                  >
                    Description
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      border: "1px solid black",
                      padding: "0.4rem",
                      fontSize: "0.9rem",
                      fontWeight: "bold",
                      width: "8%",
                    }}
                  >
                    Qty
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      border: "1px solid black",
                      padding: "0.4rem",
                      fontSize: "0.9rem",
                      fontWeight: "bold",
                      width: "8%",
                    }}
                  >
                    Rate
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      border: "1px solid black",
                      padding: "0.4rem",
                      fontSize: "0.9rem",
                      fontWeight: "bold",
                      width: "8%",
                    }}
                  >
                    Unit
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      border: "1px solid black",
                      padding: "0.4rem",
                      fontSize: "0.9rem",
                      fontWeight: "bold",
                      width: "8%",
                    }}
                  >
                    Amount
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      border: "1px solid black",
                      padding: "0.4rem",
                      fontSize: "0.9rem",
                      fontWeight: "bold",
                      width: "8%",
                    }}
                  >
                    Tax %
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      border: "1px solid black",
                      padding: "0.4rem",
                      fontSize: "0.9rem",
                      fontWeight: "bold",
                      width: "8%",
                    }}
                  >
                    Tax amt
                  </th>
                  <th
                    style={{
                      textAlign: "right",
                      border: "1px solid black",
                      padding: "0.4rem",
                      fontSize: "0.9rem",
                      fontWeight: "bold",
                      width: "10%",
                    }}
                  >
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {sectionItems.map((prod, index) => (
                  <tr key={index}>
                    <td
                      style={{
                        textAlign: "center",
                        border: "1px solid black",
                        padding: "0.4rem",
                        fontSize: "0.9rem",
                        verticalAlign: "top",
                      }}
                    >
                      {prod.id}
                    </td>
                    <td
                      style={{
                        textAlign: "left",
                        border: "1px solid black",
                        padding: "0.4rem",
                        fontSize: "0.9rem",
                        verticalAlign: "top",
                      }}
                    >
                      {prod.ItemNameTextField}
                    </td>
                    <td
                      style={{
                        textAlign: "center",
                        border: "1px solid black",
                        padding: "0.4rem",
                        fontSize: "0.9rem",
                        verticalAlign: "top",
                      }}
                    >
                      {prod.qty}
                    </td>
                    <td
                      style={{
                        textAlign: "center",
                        border: "1px solid black",
                        padding: "0.4rem",
                        fontSize: "0.9rem",
                        verticalAlign: "top",
                      }}
                    >
                      {parseFloat(prod.Rate1).toFixed(decimalCount)}
                    </td>
                    <td
                      style={{
                        textAlign: "center",
                        border: "1px solid black",
                        padding: "0.4rem",
                        fontSize: "0.9rem",
                        verticalAlign: "top",
                      }}
                    >
                      {prod.Typ}
                    </td>
                    <td
                      style={{
                        textAlign: "center",
                        border: "1px solid black",
                        padding: "0.4rem",
                        fontSize: "0.9rem",
                        verticalAlign: "top",
                      }}
                    >
                      {(prod.Rate1 * prod.qty).toFixed(decimalCount)}
                    </td>
                    <td
                      style={{
                        textAlign: "center",
                        border: "1px solid black",
                        padding: "0.4rem",
                        fontSize: "0.9rem",
                        verticalAlign: "top",
                      }}
                    >
                      {prod.Tax1}
                    </td>
                    <td
                      style={{
                        textAlign: "center",
                        border: "1px solid black",
                        padding: "0.4rem",
                        fontSize: "0.9rem",
                        verticalAlign: "top",
                      }}
                    >
                      {parseFloat(prod.taxAmt).toFixed(decimalCount)}
                    </td>
                    <td
                      style={{
                        textAlign: "right",
                        border: "1px solid black",
                        padding: "0.4rem",
                        fontSize: "0.9rem",
                        verticalAlign: "top",
                      }}
                    >
                      {prod.total}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer Section - Appears on every page */}
          <Stack
            direction="row"
            style={{
              borderTop: "1px solid black",
              backgroundColor: "white",
            }}
          >
            <Stack
              direction="column"
              sx={{
                width: "60%",
                borderRight: "1px solid black",
              }}
            >
              <Container
                disableGutters
                sx={{ padding: "0.6rem", borderTop: "1px solid black" }}
              >
                <Typography sx={{ fontSize: "0.9rem", fontWeight: "bold" }}>
                  {" "}
                  Amount In Words :
                </Typography>
                <Typography
                  sx={{ fontSize: "0.9rem", textTransform: "capitalize" }}
                >
                  {result}
                </Typography>
              </Container>
              <Container
                disableGutters
                sx={{ borderTop: "1px solid black", padding: "0.6rem" }}
              >
                <Typography sx={{ fontSize: "0.9rem", fontWeight: "bold" }}>
                  {" "}
                  Bank Details:-
                </Typography>
                <Typography sx={{ fontSize: "0.9rem" }}>
                  {" "}
                  Bank Name: {company.bank}
                </Typography>
                <Typography sx={{ fontSize: "0.9rem" }}>
                  {" "}
                  A/c Name: {company.beneficiary}
                </Typography>
                <Typography sx={{ fontSize: "0.9rem" }}>
                  {" "}
                  A/c No: {company.account_no}
                </Typography>
                <Typography sx={{ fontSize: "0.9rem" }}>
                  {" "}
                  Branch: {company.branch}
                </Typography>
                <Typography sx={{ fontSize: "0.9rem" }}>
                  {" "}
                  IFSC: {company.ifsc_code}
                </Typography>
              </Container>
              <Container
                disableGutters
                sx={{
                  borderTop: "1px solid black",
                  padding: "0.8rem 0.6rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                <Typography sx={{ fontSize: "0.9rem", fontWeight: "bold" }}>
                  Declaration
                </Typography>
                <div
                  style={{
                    textOverflow: "ellipsis",
                    fontSize: "0.8rem",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: commonState.salesBillDeclaration,
                  }}
                />
              </Container>
            </Stack>
            <Stack direction="column" sx={{ width: "40%" }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  borderTop: "1px solid black",
                  height: "2.2rem",
                  paddingLeft: "0.6rem",
                  paddingRight: "0.6rem",
                }}
              >
                <Typography sx={{ fontSize: "0.9rem", fontWeight: "bold" }}>
                  Subtotal :
                </Typography>
                <Typography sx={{ fontSize: "0.9rem" }}>
                  {data.BillTotalField.toFixed(decimalCount)}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  borderTop: "1px solid black",
                  height: "2.2rem",
                  paddingLeft: "0.6rem",
                  paddingRight: "0.6rem",
                }}
              >
                <Typography sx={{ fontSize: "0.9rem", fontWeight: "bold" }}>
                  {" "}
                  Tax Amount:{" "}
                </Typography>
                <Typography sx={{ fontSize: "0.9rem" }}>
                  {typ !== "SR" || typ !== "SRB2C"
                    ? parseFloat(data?.TItTaxAmt + data?.BillTaxField).toFixed(
                        decimalCount
                      )
                    : parseFloat(data.BillTaxField).toFixed(decimalCount)}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  borderTop: "1px solid black",
                  height: "2.2rem",
                  paddingLeft: "0.6rem",
                  paddingRight: "0.6rem",
                }}
              >
                <Typography sx={{ fontSize: "0.9rem", fontWeight: "bold" }}>
                  {" "}
                  Discount :{" "}
                </Typography>
                <Typography sx={{ fontSize: "0.9rem" }}>
                  {(data.TDiscAmt + data.BillDiscAmtField).toFixed(
                    decimalCount
                  )}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  borderBlock: "1px solid black",
                  height: "2.2rem",
                  paddingLeft: "0.6rem",
                  paddingRight: "0.6rem",
                }}
              >
                <Typography sx={{ fontSize: "0.9rem", fontWeight: "bold" }}>
                  {" "}
                  Net Total :{" "}
                </Typography>
                <Typography sx={{ fontSize: "0.9rem" }}>
                  {parseFloat(data.BillNetTotalField).toFixed(decimalCount)}
                </Typography>
              </Stack>
              <Stack
                direction="column"
                justifyContent="space-between"
                sx={{
                  height: "5rem",
                  paddingLeft: "0.6rem",
                  paddingRight: "0.6rem",
                  paddingTop: "0.5rem",
                  paddingBottom: "0.5rem",
                }}
              >
                <Typography
                  align="center"
                  sx={{ fontSize: "0.9rem", marginLeft: "auto" }}
                >
                  E&OE
                </Typography>
                <Typography align="center" sx={{ fontSize: "0.9rem" }}>
                  For {company.Name}
                </Typography>
                <Typography align="center" sx={{ fontSize: "0.9rem" }}>
                  Authorised Signatory
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </div>
        <div
          style={{
            textAlign: "center",
            fontWeight: "bold",
            marginTop: "2px",
            fontSize: "0.9rem",
          }}
        >
          Page {currentPage} of {totalPages}
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: "none" }}>
      <div
        ref={componentRef}
        style={{
          background: "white",
          margin: "1rem",
          height: "auto",
          width: "210mm", // A4 width
          minHeight: "297mm", // A4 height
          fontFamily: "'Roboto', sans-serif",
        }}
      >
        {sections}
      </div>
    </div>
  );
};

export default A4Print;
